import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';
import AdminLayout from './AdminLayout';
import '../App.css';

const CreateProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    party_name: '',
    logo_url: '',
    profile_picture: '',
    flash_image: '',
    address: '',
    mobile_number: '',
    email_id: '',
    about_us: '',
    description: '',
    slider_images: [],
    image_gallery: [],
    video_gallery: [],
    instagram_reels: [],
    recent_news: [],
    social_links: {
      facebook: '',
      twitter: '',
      instagram: '',
      website: '',
      youtube: '',
      linkedin: ''
    },
    surveys: [],
    feedbacks: []
  });
  
  // Temporary state for adding items to arrays
  const [newSliderImage, setNewSliderImage] = useState('');
  const [newGalleryImage, setNewGalleryImage] = useState('');
  const [newVideo, setNewVideo] = useState({ title: '', url: '' });
  const [newInstagramReel, setNewInstagramReel] = useState({ title: '', url: '' });
  const [newNews, setNewNews] = useState({ title: '', description: '', date: '', image: '' });
  const [newsImageFile, setNewsImageFile] = useState(null);
  const [newsImagePreview, setNewsImagePreview] = useState('');
  const [uploadingNewsImage, setUploadingNewsImage] = useState(false);
  const [newSurvey, setNewSurvey] = useState({ title: '', description: '', questions: [] });
  const [newQuestion, setNewQuestion] = useState({ question: '', type: 'text', options: [] });
  const [newOption, setNewOption] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState('');
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);
  const [flashImageFile, setFlashImageFile] = useState(null);
  const [flashImagePreview, setFlashImagePreview] = useState('');
  const [uploadingFlash, setUploadingFlash] = useState(false);
  const [sliderFiles, setSliderFiles] = useState([]);
  const [uploadingSlider, setUploadingSlider] = useState(false);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [newFeedback, setNewFeedback] = useState({ name: '', mobile_number: '', address: '', image: '', description: '' });
  const [feedbackImageFile, setFeedbackImageFile] = useState(null);
  const [feedbackImagePreview, setFeedbackImagePreview] = useState('');
  const [uploadingFeedbackImage, setUploadingFeedbackImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });


  // Handle logo file selection - Auto upload
  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size should be less than 5MB' });
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Auto upload the file
      await handleLogoUpload(file);
    }
  };

  // Upload logo
  const handleLogoUpload = async (fileToUpload = null) => {
    const file = fileToUpload || logoFile;
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a logo file' });
      return;
    }

    setUploadingLogo(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('logo', file);

      const response = await fetch(`${API_BASE_URL}/api/upload/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Set the logo URL from server response
        const logoUrl = `${API_BASE_URL}${data.data.url}`;
        setFormData(prevData => ({
          ...prevData,
          logo_url: logoUrl,
          social_links: prevData.social_links || {
            facebook: '',
            twitter: '',
            instagram: '',
            website: '',
            youtube: '',
            linkedin: ''
          },
          slider_images: prevData.slider_images || [],
          image_gallery: prevData.image_gallery || [],
          video_gallery: prevData.video_gallery || [],
          instagram_reels: prevData.instagram_reels || [],
          recent_news: prevData.recent_news || [],
          surveys: prevData.surveys || [],
          feedbacks: prevData.feedbacks || []
        }));
        setMessage({ type: 'success', text: 'Logo uploaded successfully!' });
        // Clear file after successful upload
        setTimeout(() => {
          setLogoFile(null);
          setLogoPreview('');
          setMessage({ type: '', text: '' });
        }, 2000);
      } else {
        setMessage({ type: 'error', text: data.message || 'Error uploading logo' });
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage({ type: 'error', text: 'Error uploading logo. Please try again.' });
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handle profile picture file selection - Auto upload
  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size should be less than 5MB' });
        return;
      }

      setProfilePictureFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Auto upload the file
      await handleProfilePictureUpload(file);
    }
  };

  // Upload profile picture
  const handleProfilePictureUpload = async (fileToUpload = null) => {
    const file = fileToUpload || profilePictureFile;
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a profile picture file' });
      return;
    }

    setUploadingProfilePicture(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage({ type: 'error', text: 'Please login to upload images' });
        setUploadingProfilePicture(false);
        return;
      }

      const uploadFormData = new FormData();
      uploadFormData.append('logo', file); // Reuse logo upload endpoint

      const response = await fetch(`${API_BASE_URL}/api/upload/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        setMessage({ type: 'error', text: 'Invalid response from server. Please try again.' });
        setUploadingProfilePicture(false);
        return;
      }

      if (response.ok && data.success && data.data && data.data.url) {
        // Set the profile picture URL from server response
        const pictureUrl = `${API_BASE_URL}${data.data.url}`;
        
        setFormData(prevData => ({
          ...prevData,
          profile_picture: pictureUrl,
          social_links: prevData.social_links || {
            facebook: '',
            twitter: '',
            instagram: '',
            website: '',
            youtube: '',
            linkedin: ''
          },
          slider_images: prevData.slider_images || [],
          image_gallery: prevData.image_gallery || [],
          video_gallery: prevData.video_gallery || [],
          instagram_reels: prevData.instagram_reels || [],
          recent_news: prevData.recent_news || [],
          surveys: prevData.surveys || [],
          feedbacks: prevData.feedbacks || []
        }));
        
        setMessage({ type: 'success', text: 'Profile picture uploaded successfully!' });
        
        // Clear file after successful upload
        setTimeout(() => {
          setProfilePictureFile(null);
          setProfilePicturePreview('');
          setMessage({ type: '', text: '' });
        }, 2000);
      } else {
        const errorMsg = data.message || data.error || 'Error uploading profile picture';
        setMessage({ type: 'error', text: errorMsg });
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setMessage({ type: 'error', text: `Error uploading profile picture: ${error.message}` });
    } finally {
      setUploadingProfilePicture(false);
    }
  };

  // Handle flash image file selection - Auto upload
  const handleFlashImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size should be less than 5MB' });
        return;
      }

      setFlashImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFlashImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Auto upload the file
      await handleFlashImageUpload(file);
    }
  };

  // Upload flash image
  const handleFlashImageUpload = async (fileToUpload = null) => {
    const file = fileToUpload || flashImageFile;
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a flash image file' });
      return;
    }

    setUploadingFlash(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage({ type: 'error', text: 'Please login to upload images' });
        setUploadingFlash(false);
        return;
      }

      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await fetch(`${API_BASE_URL}/api/upload/flash`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        setMessage({ type: 'error', text: 'Invalid response from server. Please try again.' });
        setUploadingFlash(false);
        return;
      }

      if (response.ok && data.success && data.data && data.data.url) {
        // Set the flash image URL from server response
        setFormData(prevData => ({
          ...prevData,
          flash_image: data.data.url, // Store relative URL
          social_links: prevData.social_links || {
            facebook: '',
            twitter: '',
            instagram: '',
            website: '',
            youtube: '',
            linkedin: ''
          },
          slider_images: prevData.slider_images || [],
          image_gallery: prevData.image_gallery || [],
          video_gallery: prevData.video_gallery || [],
          instagram_reels: prevData.instagram_reels || [],
          recent_news: prevData.recent_news || [],
          surveys: prevData.surveys || [],
          feedbacks: prevData.feedbacks || []
        }));
        
        setMessage({ type: 'success', text: 'Flash image uploaded successfully!' });
        
        // Clear file after successful upload
        setTimeout(() => {
          setFlashImageFile(null);
          setFlashImagePreview('');
          setMessage({ type: '', text: '' });
        }, 2000);
      } else {
        const errorMsg = data.message || data.error || 'Error uploading flash image';
        setMessage({ type: 'error', text: errorMsg });
      }
    } catch (error) {
      console.error('Error uploading flash image:', error);
      setMessage({ type: 'error', text: 'Error uploading flash image. Please try again.' });
    } finally {
      setUploadingFlash(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('social_')) {
      const field = name.replace('social_', '');
      setFormData({
        ...formData,
        social_links: {
          ...formData.social_links,
          [field]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Handle slider file selection - Auto upload
  const handleSliderFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Validate files
      const validFiles = files.filter(file => {
        if (!file.type.match('image.*')) {
          setMessage({ type: 'error', text: 'Please select only image files' });
          return false;
        }
        if (file.size > 5 * 1024 * 1024) {
          setMessage({ type: 'error', text: 'File size should be less than 5MB' });
          return false;
        }
        return true;
      });

      if (validFiles.length > 0) {
        // Auto upload the files
        await handleSliderUpload(validFiles);
      }
      
      e.target.value = ''; // Reset input
    }
  };

  // Upload slider images
  const handleSliderUpload = async (filesToUpload = null) => {
    const files = filesToUpload || sliderFiles;
    if (files.length === 0) {
      setMessage({ type: 'error', text: 'Please select slider images' });
      return;
    }

    setUploadingSlider(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const uploadedUrls = [];

      // Upload each file
      for (const file of files) {
        const uploadFormData = new FormData();
        uploadFormData.append('image', file);

        const response = await fetch(`${API_BASE_URL}/api/upload/slider`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: uploadFormData
        });

        const data = await response.json();

        if (response.ok && data.success) {
          const imageUrl = `${API_BASE_URL}${data.data.url}`;
          uploadedUrls.push(imageUrl);
        } else {
          throw new Error(data.message || 'Error uploading image');
        }
      }

      // Add uploaded URLs to form data
      setFormData(prevData => ({
        ...prevData,
        slider_images: [...(prevData.slider_images || []), ...uploadedUrls],
        social_links: prevData.social_links || {
          facebook: '',
          twitter: '',
          instagram: '',
          website: '',
          youtube: '',
          linkedin: ''
        },
        image_gallery: prevData.image_gallery || [],
        video_gallery: prevData.video_gallery || [],
        instagram_reels: prevData.instagram_reels || [],
        recent_news: prevData.recent_news || [],
        surveys: prevData.surveys || [],
        feedbacks: prevData.feedbacks || []
      }));

      setSliderFiles([]);
      setMessage({ type: 'success', text: `${uploadedUrls.length} slider image(s) uploaded successfully!` });
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 2000);
    } catch (error) {
      console.error('Error uploading slider images:', error);
      setMessage({ type: 'error', text: error.message || 'Error uploading slider images' });
    } finally {
      setUploadingSlider(false);
    }
  };

  // Remove slider image
  const removeSliderImage = (index) => {
    setFormData({
      ...formData,
      slider_images: formData.slider_images.filter((_, i) => i !== index)
    });
  };

  // Remove slider file from queue
  const removeSliderFile = (index) => {
    setSliderFiles(sliderFiles.filter((_, i) => i !== index));
  };

  // Handle gallery file selection - Auto upload
  const handleGalleryFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Validate files
      const validFiles = files.filter(file => {
        if (!file.type.match('image.*')) {
          setMessage({ type: 'error', text: 'Please select only image files' });
          return false;
        }
        if (file.size > 5 * 1024 * 1024) {
          setMessage({ type: 'error', text: 'File size should be less than 5MB' });
          return false;
        }
        return true;
      });

      if (validFiles.length > 0) {
        // Auto upload the files
        await handleGalleryUpload(validFiles);
      }
      
      e.target.value = ''; // Reset input
    }
  };

  // Upload gallery images
  const handleGalleryUpload = async (filesToUpload = null) => {
    const files = filesToUpload || galleryFiles;
    if (files.length === 0) {
      setMessage({ type: 'error', text: 'Please select gallery images' });
      return;
    }

    setUploadingGallery(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const uploadedUrls = [];

      // Upload each file
      for (const file of files) {
        const uploadFormData = new FormData();
        uploadFormData.append('image', file);

        const response = await fetch(`${API_BASE_URL}/api/upload/gallery`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: uploadFormData
        });

        const data = await response.json();

        if (response.ok && data.success) {
          const imageUrl = `${API_BASE_URL}${data.data.url}`;
          uploadedUrls.push(imageUrl);
        } else {
          throw new Error(data.message || 'Error uploading image');
        }
      }

      // Add uploaded URLs to form data
      setFormData(prevData => ({
        ...prevData,
        image_gallery: [...(prevData.image_gallery || []), ...uploadedUrls],
        social_links: prevData.social_links || {
          facebook: '',
          twitter: '',
          instagram: '',
          website: '',
          youtube: '',
          linkedin: ''
        },
        slider_images: prevData.slider_images || [],
        video_gallery: prevData.video_gallery || [],
        instagram_reels: prevData.instagram_reels || [],
        recent_news: prevData.recent_news || [],
        surveys: prevData.surveys || [],
        feedbacks: prevData.feedbacks || []
      }));

      setGalleryFiles([]);
      setMessage({ type: 'success', text: `${uploadedUrls.length} gallery image(s) uploaded successfully!` });
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 2000);
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      setMessage({ type: 'error', text: error.message || 'Error uploading gallery images' });
    } finally {
      setUploadingGallery(false);
    }
  };

  // Remove gallery image
  const removeGalleryImage = (index) => {
    setFormData({
      ...formData,
      image_gallery: formData.image_gallery.filter((_, i) => i !== index)
    });
  };

  // Remove gallery file from queue
  const removeGalleryFile = (index) => {
    setGalleryFiles(galleryFiles.filter((_, i) => i !== index));
  };

  // Add video
  const addVideo = () => {
    if (newVideo.title.trim() && newVideo.url.trim()) {
      setFormData({
        ...formData,
        video_gallery: [...formData.video_gallery, { ...newVideo }]
      });
      setNewVideo({ title: '', url: '' });
    }
  };

  // Remove video
  const removeVideo = (index) => {
    setFormData({
      ...formData,
      video_gallery: formData.video_gallery.filter((_, i) => i !== index)
    });
  };

  // Add Instagram Reel
  const addInstagramReel = () => {
    if (newInstagramReel.title.trim() && newInstagramReel.url.trim()) {
      setFormData({
        ...formData,
        instagram_reels: [...formData.instagram_reels, { ...newInstagramReel }]
      });
      setNewInstagramReel({ title: '', url: '' });
    }
  };

  // Remove Instagram Reel
  const removeInstagramReel = (index) => {
    setFormData({
      ...formData,
      instagram_reels: formData.instagram_reels.filter((_, i) => i !== index)
    });
  };

  // Handle news image file selection
  const handleNewsImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size should be less than 5MB' });
        return;
      }

      setNewsImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewsImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload news image
  const handleNewsImageUpload = async () => {
    if (!newsImageFile) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    setUploadingNewsImage(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage({ type: 'error', text: 'Please login to upload images' });
        setUploadingNewsImage(false);
        return;
      }

      const uploadFormData = new FormData();
      uploadFormData.append('logo', newsImageFile); // Reuse logo upload endpoint

      const response = await fetch(`${API_BASE_URL}/api/upload/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        setMessage({ type: 'error', text: 'Invalid response from server. Please try again.' });
        setUploadingNewsImage(false);
        return;
      }

      if (response.ok && data.success && data.data && data.data.url) {
        const imageUrl = `${API_BASE_URL}${data.data.url}`;
        setNewNews({ ...newNews, image: imageUrl });
        setMessage({ type: 'success', text: 'News image uploaded successfully!' });
        setNewsImageFile(null);
        setNewsImagePreview('');
      } else {
        const errorMsg = data.message || data.error || 'Error uploading image';
        setMessage({ type: 'error', text: errorMsg });
      }
    } catch (error) {
      console.error('Error uploading news image:', error);
      setMessage({ type: 'error', text: `Error uploading image: ${error.message}` });
    } finally {
      setUploadingNewsImage(false);
    }
  };

  // Add news
  const addNews = () => {
    if (newNews.title.trim() && newNews.description.trim()) {
      setFormData({
        ...formData,
        recent_news: [...formData.recent_news, { ...newNews }]
      });
      setNewNews({ title: '', description: '', date: '', image: '' });
      setNewsImagePreview('');
    }
  };

  // Remove news
  const removeNews = (index) => {
    setFormData({
      ...formData,
      recent_news: formData.recent_news.filter((_, i) => i !== index)
    });
  };

  // Survey handlers
  const addQuestion = () => {
    if (newQuestion.question.trim()) {
      setNewSurvey({
        ...newSurvey,
        questions: [...newSurvey.questions, { ...newQuestion }]
      });
      setNewQuestion({ question: '', type: 'text', options: [] });
      setNewOption('');
    }
  };

  const removeQuestion = (index) => {
    setNewSurvey({
      ...newSurvey,
      questions: newSurvey.questions.filter((_, i) => i !== index)
    });
  };

  const addOption = () => {
    if (newOption.trim() && newQuestion.type === 'multiple_choice') {
      setNewQuestion({
        ...newQuestion,
        options: [...newQuestion.options, newOption.trim()]
      });
      setNewOption('');
    }
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updatedQuestions = [...newSurvey.questions];
    updatedQuestions[questionIndex].options = updatedQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
    setNewSurvey({ ...newSurvey, questions: updatedQuestions });
  };

  const addSurvey = () => {
    if (newSurvey.title.trim() && newSurvey.questions.length > 0) {
      setFormData({
        ...formData,
        surveys: [...formData.surveys, { ...newSurvey }]
      });
      setNewSurvey({ title: '', description: '', questions: [] });
      setNewQuestion({ question: '', type: 'text', options: [] });
      setNewOption('');
    }
  };

  const removeSurvey = (index) => {
    setFormData({
      ...formData,
      surveys: formData.surveys.filter((_, i) => i !== index)
    });
  };

  // Feedback handlers
  const handleFeedbackImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'File size should be less than 5MB' });
        return;
      }

      setFeedbackImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFeedbackImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload feedback image
  const handleFeedbackImageUpload = async () => {
    if (!feedbackImageFile) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    setUploadingFeedbackImage(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage({ type: 'error', text: 'Please login to upload images' });
        setUploadingFeedbackImage(false);
        return;
      }

      const uploadFormData = new FormData();
      uploadFormData.append('image', feedbackImageFile);

      const response = await fetch(`${API_BASE_URL}/api/upload/gallery`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        setMessage({ type: 'error', text: 'Invalid response from server. Please try again.' });
        setUploadingFeedbackImage(false);
        return;
      }

      if (response.ok && data.success && data.data && data.data.url) {
        // Set the feedback image URL from server response
        setNewFeedback(prev => ({
          ...prev,
          image: data.data.url // Store relative URL
        }));
        
        setMessage({ type: 'success', text: 'Feedback image uploaded successfully!' });
        
        // Clear file after successful upload
        setTimeout(() => {
          setFeedbackImageFile(null);
          setFeedbackImagePreview('');
        }, 2000);
      } else {
        const errorMsg = data.message || data.error || 'Error uploading feedback image';
        setMessage({ type: 'error', text: errorMsg });
      }
    } catch (error) {
      console.error('Error uploading feedback image:', error);
      setMessage({ type: 'error', text: 'Error uploading feedback image. Please try again.' });
    } finally {
      setUploadingFeedbackImage(false);
    }
  };

  // Add feedback
  const addFeedback = () => {
    if (newFeedback.name.trim() && newFeedback.mobile_number.trim() && newFeedback.address.trim() && newFeedback.description.trim()) {
      setFormData({
        ...formData,
        feedbacks: [...formData.feedbacks, { ...newFeedback }]
      });
      setNewFeedback({ name: '', mobile_number: '', address: '', image: '', description: '' });
      setFeedbackImageFile(null);
      setFeedbackImagePreview('');
      setMessage({ type: 'success', text: 'Feedback added successfully!' });
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 2000);
    } else {
      setMessage({ type: 'error', text: 'Please fill all required fields (Name, Mobile Number, Address, Description)' });
    }
  };

  // Remove feedback
  const removeFeedback = (index) => {
    setFormData({
      ...formData,
      feedbacks: formData.feedbacks.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/profiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({
          type: 'success',
          text: 'Profile created successfully! Redirecting to your profile...'
        });
        
        // Redirect to profile page after 1 second
        setTimeout(() => {
          navigate(`/profile/${user?.username}`);
        }, 1000);
      } else {
        setMessage({
          type: 'error',
          text: data.message || 'Error creating profile. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error creating profile:', error);
      setMessage({
        type: 'error',
        text: 'Error creating profile. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    { icon: 'fi fi-rr-apps', label: 'Dashboard', path: '/dashboard', active: false },
    { icon: 'fi fi-rr-users', label: 'All Profiles', path: '/profiles', active: false },
    { icon: 'fi fi-rr-user-add', label: 'Create Profile', path: '/create-profile', active: true },
    { icon: 'fi fi-rr-users', label: 'Users', path: '/users', active: false },
    { icon: 'fi fi-rr-user-add', label: 'Create User', path: '/create-user', active: false },
    { icon: 'fi fi-rr-shield', label: 'Admins', path: '/admins', active: false },
    { icon: 'fi fi-rr-chart-line-up', label: 'Analytics', path: '/analytics', active: false },
    { icon: 'fi fi-rr-envelope', label: 'Messages', path: '/messages', active: false },
    { icon: 'fi fi-rr-document', label: 'Reports', path: '/reports', active: false },
  ];

  // Check if current path matches menu item
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <AdminLayout pageTitle="Create Profile" pageSubtitle="Build your professional political profile">

          {/* Create Profile Form Card */}
          <div className="business-form-card">
            <div className="business-form-header">
              <div className="business-form-icon-wrapper">
                <i className="fi fi-rr-user-add"></i>
              </div>
              <h2 className="business-form-title">Create New Profile</h2>
              <p className="business-form-subtitle">Fill in the details to create your political profile</p>
            </div>

            {message.text && (
              <div className={`business-alert business-alert-${message.type === 'success' ? 'success' : 'error'}`}>
                <i className={`fi fi-rr-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                <span>{message.text}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="business-create-form">
              {/* Logo & Images Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-picture"></i>
                  Logo & Images
                </h3>
                
                <div className="business-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                  {/* Logo Upload */}
                  <div className="business-form-group">
                    <label htmlFor="logo_file" className="business-form-label">
                      <i className="fi fi-rr-picture"></i>
                      Logo Image * (Max 5MB)
                    </label>
                    
                    <div className="business-upload-wrapper">
                      <input
                        type="file"
                        id="logo_file"
                        name="logo_file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="business-file-input"
                      />
                      <label htmlFor="logo_file" className="business-file-label">
                        <i className="fi fi-rr-cloud-upload"></i>
                        <span>{logoFile ? logoFile.name : 'Choose Logo Image'}</span>
                      </label>
                      {uploadingLogo && (
                        <div className="business-upload-status" style={{ padding: '8px 12px', background: '#f0f9ff', borderRadius: '8px', color: '#0369a1', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="business-spinner" style={{ width: '16px', height: '16px' }}></span>
                          <span>Uploading...</span>
                        </div>
                      )}
                    </div>

                    {/* Preview */}
                    {(logoPreview || formData.logo_url) && (
                      <div className="business-image-preview">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo preview" className="business-preview-image" />
                        ) : formData.logo_url ? (
                          <>
                            <p className="business-upload-success">
                              <i className="fi fi-rr-check-circle"></i>
                              Logo uploaded successfully
                            </p>
                            <img src={formData.logo_url} alt="Uploaded logo" className="business-preview-image" />
                          </>
                        ) : null}
                      </div>
                    )}

                    <input
                      type="hidden"
                      name="logo_url"
                      value={formData.logo_url}
                      required={!formData.logo_url}
                    />
                  </div>

                  {/* Profile Picture Upload */}
                  <div className="business-form-group">
                    <label htmlFor="profile_picture_file" className="business-form-label">
                      <i className="fi fi-rr-user"></i>
                      Profile Picture (Max 5MB)
                    </label>
                  
                    <div className="business-upload-wrapper">
                      <input
                        type="file"
                        id="profile_picture_file"
                        name="profile_picture_file"
                        accept="image/*"
                        onChange={handleProfilePictureChange}
                        className="business-file-input"
                      />
                      <label htmlFor="profile_picture_file" className="business-file-label">
                        <i className="fi fi-rr-cloud-upload"></i>
                        <span>{profilePictureFile ? profilePictureFile.name : 'Choose Profile Picture'}</span>
                      </label>
                      {uploadingProfilePicture && (
                        <div className="business-upload-status" style={{ padding: '8px 12px', background: '#f0f9ff', borderRadius: '8px', color: '#0369a1', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="business-spinner" style={{ width: '16px', height: '16px' }}></span>
                          <span>Uploading...</span>
                        </div>
                      )}
                    </div>

                    {(profilePicturePreview || formData.profile_picture) && (
                      <div className="business-image-preview">
                        {profilePicturePreview ? (
                          <img src={profilePicturePreview} alt="Profile picture preview" className="business-preview-image business-preview-circle" />
                        ) : formData.profile_picture ? (
                          <>
                            <p className="business-upload-success">
                              <i className="fi fi-rr-check-circle"></i>
                              Profile picture uploaded successfully
                            </p>
                            <img src={formData.profile_picture} alt="Uploaded profile picture" className="business-preview-image business-preview-circle" />
                          </>
                        ) : null}
                      </div>
                    )}

                    <input type="hidden" name="profile_picture" value={formData.profile_picture} />
                  </div>

                  {/* Flash Image Upload */}
                  <div className="business-form-group">
                    <label htmlFor="flash_image_file" className="business-form-label">
                      <i className="fi fi-rr-picture"></i>
                      Flash Image (Max 5MB)
                    </label>
                  
                    <div className="business-upload-wrapper">
                      <input
                        type="file"
                        id="flash_image_file"
                        name="flash_image_file"
                        accept="image/*"
                        onChange={handleFlashImageChange}
                        className="business-file-input"
                      />
                      <label htmlFor="flash_image_file" className="business-file-label">
                        <i className="fi fi-rr-cloud-upload"></i>
                        <span>{flashImageFile ? flashImageFile.name : 'Choose Flash Image'}</span>
                      </label>
                      {uploadingFlash && (
                        <div className="business-upload-status" style={{ padding: '8px 12px', background: '#f0f9ff', borderRadius: '8px', color: '#0369a1', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="business-spinner" style={{ width: '16px', height: '16px' }}></span>
                          <span>Uploading...</span>
                        </div>
                      )}
                    </div>

                    {(flashImagePreview || formData.flash_image) && (
                      <div className="business-image-preview">
                        {flashImagePreview ? (
                          <img src={flashImagePreview} alt="Flash image preview" className="business-preview-image" />
                        ) : formData.flash_image ? (
                          <>
                            <p className="business-upload-success">
                              <i className="fi fi-rr-check-circle"></i>
                              Flash image uploaded successfully
                            </p>
                            <img src={formData.flash_image} alt="Uploaded flash image" className="business-preview-image" />
                          </>
                        ) : null}
                      </div>
                    )}

                    <input type="hidden" name="flash_image" value={formData.flash_image} />
                    
                    <div className="business-form-help" style={{ fontSize: '0.8rem', marginTop: '8px' }}>
                      <i className="fi fi-rr-info"></i>
                      <span>Banner image (1200x400px)</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Basic Information Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-user"></i>
                  Basic Information
                </h3>
                
                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="name" className="business-form-label">
                      <i className="fi fi-rr-id-card"></i>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter full name"
                      className="business-form-input"
                    />
                  </div>

                  <div className="business-form-group">
                    <label htmlFor="party_name" className="business-form-label">
                      <i className="fi fi-rr-flag"></i>
                      Party Name *
                    </label>
                    <input
                      type="text"
                      id="party_name"
                      name="party_name"
                      value={formData.party_name}
                      onChange={handleChange}
                      required
                      placeholder="Enter party name"
                      className="business-form-input"
                    />
                  </div>
                </div>

                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="mobile_number" className="business-form-label">
                      <i className="fi fi-rr-phone-call"></i>
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      id="mobile_number"
                      name="mobile_number"
                      value={formData.mobile_number}
                      onChange={handleChange}
                      required
                      placeholder="Enter mobile number"
                      className="business-form-input"
                    />
                  </div>

                  <div className="business-form-group">
                    <label htmlFor="email_id" className="business-form-label">
                      <i className="fi fi-rr-envelope"></i>
                      Email ID *
                    </label>
                    <input
                      type="email"
                      id="email_id"
                      name="email_id"
                      value={formData.email_id}
                      onChange={handleChange}
                      required
                      placeholder="Enter email address"
                      className="business-form-input"
                    />
                  </div>
                </div>

                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="address" className="business-form-label">
                      <i className="fi fi-rr-marker"></i>
                      Address *
                    </label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      placeholder="Enter full address"
                      className="business-form-input business-form-textarea"
                      rows="3"
                    />
                  </div>
                </div>
              </div>

              {/* About & Description Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-document"></i>
                  About & Description
                </h3>
                
                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="about_us" className="business-form-label">
                      <i className="fi fi-rr-file-text"></i>
                      About Us *
                    </label>
                    <textarea
                      id="about_us"
                      name="about_us"
                      value={formData.about_us}
                      onChange={handleChange}
                      required
                      placeholder="Write about yourself, your political journey, achievements..."
                      className="business-form-input business-form-textarea"
                      rows="5"
                    />
                  </div>

                  <div className="business-form-group">
                    <label htmlFor="description" className="business-form-label">
                      <i className="fi fi-rr-align-left"></i>
                      Detailed Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Provide a detailed description of your political profile, vision, and goals..."
                      className="business-form-input business-form-textarea"
                      rows="5"
                    />
                  </div>
                </div>
              </div>

              {/* Social Links Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-share"></i>
                  Social Media Links
                </h3>
                
                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="facebook" className="business-form-label">
                      <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
                        <circle cx="12" cy="12" r="11" fill="#1877F2"/>
                        <path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z" fill="#FFFFFF"/>
                      </svg>
                      Facebook
                    </label>
                    <input
                      type="url"
                      id="facebook"
                      name="facebook"
                      value={formData.social_links?.facebook || ''}
                      onChange={(e) => setFormData(prevData => ({
                        ...prevData,
                        social_links: { 
                          ...(prevData.social_links || {}), 
                          facebook: e.target.value 
                        }
                      }))}
                      placeholder="https://facebook.com/yourpage"
                      className="business-form-input"
                    />
                  </div>

                  <div className="business-form-group">
                    <label htmlFor="twitter" className="business-form-label">
                      <i className="fi fi-brands-twitter"></i>
                      Twitter
                    </label>
                    <input
                      type="url"
                      id="twitter"
                      name="twitter"
                      value={formData.social_links?.twitter || ''}
                      onChange={(e) => setFormData(prevData => ({
                        ...prevData,
                        social_links: { 
                          ...(prevData.social_links || {}), 
                          twitter: e.target.value 
                        }
                      }))}
                      placeholder="https://twitter.com/yourhandle"
                      className="business-form-input"
                    />
                  </div>
                </div>

                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="instagram" className="business-form-label">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="url(#instagram-gradient-create)" style={{ marginRight: '8px' }}>
                        <defs>
                          <linearGradient id="instagram-gradient-create" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#833AB4" />
                            <stop offset="50%" stopColor="#FD1D1D" />
                            <stop offset="100%" stopColor="#FCB045" />
                          </linearGradient>
                        </defs>
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4 2.209 0 4 1.791 4 4 0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      Instagram
                    </label>
                    <input
                      type="url"
                      id="instagram"
                      name="instagram"
                      value={formData.social_links?.instagram || ''}
                      onChange={(e) => setFormData(prevData => ({
                        ...prevData,
                        social_links: { 
                          ...(prevData.social_links || {}), 
                          instagram: e.target.value 
                        }
                      }))}
                      placeholder="https://instagram.com/yourprofile"
                      className="business-form-input"
                    />
                  </div>

                  <div className="business-form-group">
                    <label htmlFor="youtube" className="business-form-label">
                      <i className="fi fi-brands-youtube"></i>
                      YouTube
                    </label>
                    <input
                      type="url"
                      id="youtube"
                      name="youtube"
                      value={formData.social_links?.youtube || ''}
                      onChange={(e) => setFormData(prevData => ({
                        ...prevData,
                        social_links: { 
                          ...(prevData.social_links || {}), 
                          youtube: e.target.value 
                        }
                      }))}
                      placeholder="https://youtube.com/yourchannel"
                      className="business-form-input"
                    />
                  </div>
                </div>

                <div className="business-form-row">
                  <div className="business-form-group">
                    <label htmlFor="linkedin" className="business-form-label">
                      <i className="fi fi-brands-linkedin"></i>
                      LinkedIn
                    </label>
                    <input
                      type="url"
                      id="linkedin"
                      name="linkedin"
                      value={formData.social_links?.linkedin || ''}
                      onChange={(e) => setFormData(prevData => ({
                        ...prevData,
                        social_links: { 
                          ...(prevData.social_links || {}), 
                          linkedin: e.target.value 
                        }
                      }))}
                      placeholder="https://linkedin.com/in/yourprofile"
                      className="business-form-input"
                    />
                  </div>

                  <div className="business-form-group">
                    <label htmlFor="website" className="business-form-label">
                      <i className="fi fi-rr-globe"></i>
                      Website
                    </label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={formData.social_links?.website || ''}
                      onChange={(e) => setFormData(prevData => ({
                        ...prevData,
                        social_links: { 
                          ...(prevData.social_links || {}), 
                          website: e.target.value 
                        }
                      }))}
                      placeholder="https://yourwebsite.com"
                      className="business-form-input"
                    />
                  </div>
                </div>
              </div>

              {/* Slider Images Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-images"></i>
                  Slider Images
                </h3>
                
                <div className="business-form-group">
                  <input type="file" id="slider_files" name="slider_files" accept="image/*" multiple onChange={handleSliderFileChange} className="business-file-input" />
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center' }}>
                    <label htmlFor="slider_files" className="business-file-label">
                      <i className="fi fi-rr-cloud-upload"></i>
                      <span>Choose Slider Images (Multiple)</span>
                    </label>
                    {uploadingSlider && (
                      <div className="business-upload-status" style={{ padding: '8px 12px', background: '#f0f9ff', borderRadius: '8px', color: '#0369a1', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="business-spinner" style={{ width: '16px', height: '16px' }}></span>
                        <span>Uploading...</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {formData.slider_images && Array.isArray(formData.slider_images) && formData.slider_images.length > 0 && (
                  <div>
                    <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '12px' }}>Uploaded Slider Images ({formData.slider_images.length}):</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                      {formData.slider_images.map((img, index) => (
                        <div key={index} style={{ position: 'relative', background: '#f8fafc', borderRadius: '8px', overflow: 'hidden' }}>
                          <img src={img} alt={`Slider ${index + 1}`} style={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
                          <button type="button" onClick={() => removeSliderImage(index)} className="business-btn business-btn-secondary" style={{ position: 'absolute', top: '8px', right: '8px', padding: '6px 10px', fontSize: '0.85rem' }}>×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Image Gallery Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-picture"></i>
                  Image Gallery
                </h3>
                
                <div className="business-form-group">
                  <input type="file" id="gallery_files" name="gallery_files" accept="image/*" multiple onChange={handleGalleryFileChange} className="business-file-input" />
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center' }}>
                    <label htmlFor="gallery_files" className="business-file-label">
                      <i className="fi fi-rr-cloud-upload"></i>
                      <span>Choose Gallery Images (Multiple)</span>
                    </label>
                    {uploadingGallery && (
                      <div className="business-upload-status" style={{ padding: '8px 12px', background: '#f0f9ff', borderRadius: '8px', color: '#0369a1', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="business-spinner" style={{ width: '16px', height: '16px' }}></span>
                        <span>Uploading...</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {formData.image_gallery && Array.isArray(formData.image_gallery) && formData.image_gallery.length > 0 && (
                  <div>
                    <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '12px' }}>Uploaded Gallery Images ({formData.image_gallery.length}):</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                      {formData.image_gallery.map((img, index) => (
                        <div key={index} style={{ position: 'relative', background: '#f8fafc', borderRadius: '8px', overflow: 'hidden' }}>
                          <img src={img} alt={`Gallery ${index + 1}`} style={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
                          <button type="button" onClick={() => removeGalleryImage(index)} className="business-btn business-btn-secondary" style={{ position: 'absolute', top: '8px', right: '8px', padding: '6px 10px', fontSize: '0.85rem' }}>×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Video Gallery Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-video"></i>
                  Video Gallery
                </h3>
                
                <div className="business-form-group">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
                    <input 
                      type="text" 
                      value={newVideo.title} 
                      onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })} 
                      placeholder="Video Title..." 
                      className="business-form-input"
                    />
                    <input 
                      type="url" 
                      value={newVideo.url} 
                      onChange={(e) => setNewVideo({ ...newVideo, url: e.target.value })} 
                      placeholder="Video URL (YouTube, Vimeo, etc.)..." 
                      className="business-form-input"
                    />
                    <button type="button" onClick={addVideo} className="business-btn business-btn-primary">
                      <i className="fi fi-rr-plus"></i>
                      <span>Add Video</span>
                    </button>
                  </div>
                  
                  {formData.video_gallery && Array.isArray(formData.video_gallery) && formData.video_gallery.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {formData.video_gallery.map((video, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#1e293b', fontWeight: '600', marginBottom: '4px' }}>{video.title}</div>
                            <div style={{ color: '#64748b', fontSize: '0.85rem', wordBreak: 'break-all' }}>{video.url}</div>
                          </div>
                          <button type="button" onClick={() => removeVideo(index)} className="business-btn business-btn-secondary" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Instagram Reels Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-brands-instagram"></i>
                  Instagram Reels
                </h3>
                
                <div className="business-form-group">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
                    <input 
                      type="text" 
                      value={newInstagramReel.title} 
                      onChange={(e) => setNewInstagramReel({ ...newInstagramReel, title: e.target.value })} 
                      placeholder="Reel Title..." 
                      className="business-form-input"
                    />
                    <input 
                      type="url" 
                      value={newInstagramReel.url} 
                      onChange={(e) => setNewInstagramReel({ ...newInstagramReel, url: e.target.value })} 
                      placeholder="Instagram Reel URL (https://instagram.com/reel/...)" 
                      className="business-form-input"
                    />
                    <button type="button" onClick={addInstagramReel} className="business-btn business-btn-primary">
                      <i className="fi fi-rr-plus"></i>
                      <span>Add Instagram Reel</span>
                    </button>
                  </div>
                  
                  {formData.instagram_reels && Array.isArray(formData.instagram_reels) && formData.instagram_reels.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {formData.instagram_reels.map((reel, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#1e293b', fontWeight: '600', marginBottom: '4px' }}>{reel.title}</div>
                            <div style={{ color: '#64748b', fontSize: '0.85rem', wordBreak: 'break-all' }}>{reel.url}</div>
                          </div>
                          <button type="button" onClick={() => removeInstagramReel(index)} className="business-btn business-btn-secondary" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Recent News Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-newspaper"></i>
                  Recent News
                </h3>
                
                <div className="business-form-group">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
                    <input 
                      type="text" 
                      value={newNews.title} 
                      onChange={(e) => setNewNews({ ...newNews, title: e.target.value })} 
                      placeholder="News Title..." 
                      className="business-form-input"
                    />
                    
                    {/* News Image Upload */}
                    <div>
                      <label htmlFor="news_image_file" className="business-form-label">
                        <i className="fi fi-rr-picture"></i>
                        News Image (Optional, Max 5MB)
                      </label>
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                        <input
                          type="file"
                          id="news_image_file"
                          name="news_image_file"
                          accept="image/*"
                          onChange={handleNewsImageChange}
                          className="business-file-input"
                        />
                        <label htmlFor="news_image_file" className="business-file-label">
                          <i className="fi fi-rr-cloud-upload"></i>
                          <span>{newsImageFile ? newsImageFile.name : 'Choose News Image'}</span>
                        </label>
                        {newsImageFile && (
                          <button
                            type="button"
                            onClick={handleNewsImageUpload}
                            disabled={uploadingNewsImage}
                            className="business-btn business-btn-primary business-upload-btn"
                          >
                            {uploadingNewsImage ? (
                              <>
                                <span className="business-spinner"></span>
                                <span>Uploading...</span>
                              </>
                            ) : (
                              <>
                                <i className="fi fi-rr-upload"></i>
                                <span>Upload</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      
                      {/* Preview */}
                      {(newsImagePreview || newNews.image) && (
                        <div style={{ marginTop: '12px', textAlign: 'center' }}>
                          <img
                            src={newsImagePreview || newNews.image}
                            alt="News preview"
                            style={{
                              width: '100%',
                              maxWidth: '300px',
                              maxHeight: '200px',
                              borderRadius: '8px',
                              border: '2px solid #e2e8f0',
                              objectFit: 'cover',
                              display: 'block',
                              margin: '0 auto'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          {newNews.image && (
                            <p style={{ color: '#10b981', fontSize: '0.75rem', marginTop: '8px' }}>
                              ✓ Image uploaded successfully!
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <textarea 
                      value={newNews.description} 
                      onChange={(e) => setNewNews({ ...newNews, description: e.target.value })} 
                      placeholder="News Description..." 
                      rows="3" 
                      className="business-form-input business-form-textarea"
                    />
                    <input 
                      type="date" 
                      value={newNews.date} 
                      onChange={(e) => setNewNews({ ...newNews, date: e.target.value })} 
                      placeholder="News Date..." 
                      className="business-form-input"
                    />
                    <button type="button" onClick={addNews} className="business-btn business-btn-primary">
                      <i className="fi fi-rr-plus"></i>
                      <span>Add News</span>
                    </button>
                  </div>
                  
                  {formData.recent_news && Array.isArray(formData.recent_news) && formData.recent_news.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {formData.recent_news.map((news, index) => {
                        const hasImage = news.image && news.image.trim() !== '' && news.image !== null && news.image !== undefined;
                        
                        return (
                          <div key={index} style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                              <div style={{ flex: 1 }}>
                                {hasImage && (
                                  <div style={{ marginBottom: '12px' }}>
                                    <img
                                      src={news.image}
                                      alt={news.title || 'News'}
                                      style={{
                                        width: '100%',
                                        maxHeight: '200px',
                                        borderRadius: '8px',
                                        objectFit: 'cover',
                                        border: '2px solid #e2e8f0'
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                )}
                                <div style={{ color: '#1e293b', fontWeight: '600', marginBottom: '4px' }}>{news.title}</div>
                                <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '4px' }}>{news.description}</div>
                                {news.date && <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Date: {news.date}</div>}
                              </div>
                              <button type="button" onClick={() => removeNews(index)} className="business-btn business-btn-secondary" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>Remove</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Survey Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-clipboard"></i>
                  Survey (सर्वेक्षण)
                </h3>
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '20px' }}>
                  Create surveys to collect feedback from users for your election campaign
                </p>
                
                {/* Create New Survey Form */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
                  <input
                    type="text"
                    value={newSurvey.title}
                    onChange={(e) => setNewSurvey({ ...newSurvey, title: e.target.value })}
                    placeholder="Survey Title (e.g., Election Feedback Survey)..."
                    className="business-form-input"
                  />
                  <textarea
                    value={newSurvey.description}
                    onChange={(e) => setNewSurvey({ ...newSurvey, description: e.target.value })}
                    placeholder="Survey Description (Optional)..."
                    rows="2"
                    className="business-form-input business-form-textarea"
                  />
                  
                  {/* Add Questions Section */}
                  <div style={{ marginTop: '12px', padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <label className="business-form-label" style={{ marginBottom: '8px' }}>
                      Add Questions
                    </label>
                    <input
                      type="text"
                      value={newQuestion.question}
                      onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                      placeholder="Enter question..."
                      className="business-form-input"
                      style={{ marginBottom: '10px' }}
                    />
                    <select
                      value={newQuestion.type}
                      onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value, options: e.target.value === 'text' ? [] : newQuestion.options })}
                      className="business-form-input"
                      style={{ marginBottom: '10px' }}
                    >
                      <option value="text">Text Answer</option>
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="yes_no">Yes/No</option>
                    </select>
                    
                    {/* Options for Multiple Choice */}
                    {newQuestion.type === 'multiple_choice' && (
                      <div style={{ marginBottom: '10px' }}>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="text"
                            value={newOption}
                            onChange={(e) => setNewOption(e.target.value)}
                            placeholder="Add option..."
                            className="business-form-input"
                            style={{ flex: 1 }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addOption();
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={addOption}
                            className="business-btn business-btn-secondary"
                            style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                          >
                            Add Option
                          </button>
                        </div>
                        {newQuestion.options.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {newQuestion.options.map((opt, idx) => (
                              <div key={idx} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 10px',
                                background: '#e2e8f0',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                color: '#1e293b'
                              }}>
                                <span>{opt}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewQuestion({
                                      ...newQuestion,
                                      options: newQuestion.options.filter((_, i) => i !== idx)
                                    });
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#ef4444',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    padding: '0 4px'
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <button
                      type="button"
                      onClick={addQuestion}
                      className="business-btn business-btn-primary"
                      style={{ width: '100%', padding: '10px 16px', fontSize: '0.9rem' }}
                    >
                      <i className="fi fi-rr-plus"></i>
                      <span>Add Question</span>
                    </button>
                  </div>
                  
                  {/* Added Questions List */}
                  {newSurvey.questions.length > 0 && (
                    <div style={{ marginTop: '12px', padding: '12px', background: '#fff', borderRadius: '8px' }}>
                      <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '8px' }}>
                        Questions ({newSurvey.questions.length}):
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {newSurvey.questions.map((q, idx) => (
                          <div key={idx} style={{
                            padding: '10px',
                            background: '#f8fafc',
                            borderRadius: '6px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: '#1e293b', fontSize: '0.9rem', marginBottom: '4px' }}>
                                {q.question}
                              </div>
                              <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                                Type: {q.type === 'text' ? 'Text' : q.type === 'multiple_choice' ? 'Multiple Choice' : 'Yes/No'}
                                {q.options.length > 0 && ` (${q.options.length} options)`}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeQuestion(idx)}
                              className="business-btn business-btn-secondary"
                              style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <button
                    type="button"
                    onClick={addSurvey}
                    disabled={newSurvey.title.trim() === '' || newSurvey.questions.length === 0}
                    className="business-btn business-btn-primary"
                    style={{ opacity: newSurvey.title.trim() === '' || newSurvey.questions.length === 0 ? 0.6 : 1 }}
                  >
                    <i className="fi fi-rr-plus"></i>
                    <span>Add Survey</span>
                  </button>
                </div>
                
                {/* Added Surveys List */}
                {Array.isArray(formData.surveys) && formData.surveys.length > 0 && (
                  <div style={{ marginTop: '24px' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>
                      Created Surveys ({formData.surveys.length})
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {formData.surveys.map((survey, index) => (
                        <div key={index} style={{ 
                          padding: '16px', 
                          background: '#f8fafc', 
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: '#1e293b', fontWeight: '600', marginBottom: '4px' }}>{survey.title}</div>
                              {survey.description && (
                                <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '8px' }}>{survey.description}</div>
                              )}
                              <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                                Questions: {survey.questions ? survey.questions.length : 0}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeSurvey(index)}
                              className="business-btn business-btn-secondary"
                              style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Feedback Section */}
              <div className="business-form-section">
                <h3 className="business-section-title">
                  <i className="fi fi-rr-comment"></i>
                  Feedback
                </h3>
                
                <div className="business-form-group">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px', padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
                    <input 
                      type="text" 
                      value={newFeedback.name} 
                      onChange={(e) => setNewFeedback({ ...newFeedback, name: e.target.value })} 
                      placeholder="Name *" 
                      className="business-form-input"
                    />
                    <input 
                      type="tel" 
                      value={newFeedback.mobile_number} 
                      onChange={(e) => setNewFeedback({ ...newFeedback, mobile_number: e.target.value })} 
                      placeholder="Mobile Number *" 
                      className="business-form-input"
                    />
                    <input 
                      type="text" 
                      value={newFeedback.address} 
                      onChange={(e) => setNewFeedback({ ...newFeedback, address: e.target.value })} 
                      placeholder="Address *" 
                      className="business-form-input"
                    />
                    
                    {/* Feedback Image Upload */}
                    <div>
                      <label htmlFor="feedback_image_file" className="business-form-label">
                        <i className="fi fi-rr-picture"></i>
                        Feedback Image (Optional, Max 5MB)
                      </label>
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                        <input
                          type="file"
                          id="feedback_image_file"
                          name="feedback_image_file"
                          accept="image/*"
                          onChange={handleFeedbackImageChange}
                          className="business-file-input"
                        />
                        <label htmlFor="feedback_image_file" className="business-file-label">
                          <i className="fi fi-rr-cloud-upload"></i>
                          <span>{feedbackImageFile ? feedbackImageFile.name : 'Choose Feedback Image'}</span>
                        </label>
                        {feedbackImageFile && (
                          <button
                            type="button"
                            onClick={handleFeedbackImageUpload}
                            disabled={uploadingFeedbackImage}
                            className="business-btn business-btn-primary business-upload-btn"
                          >
                            {uploadingFeedbackImage ? (
                              <>
                                <span className="business-spinner"></span>
                                <span>Uploading...</span>
                              </>
                            ) : (
                              <>
                                <i className="fi fi-rr-upload"></i>
                                <span>Upload</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      
                      {/* Preview */}
                      {(feedbackImagePreview || newFeedback.image) && (
                        <div style={{ marginTop: '12px', textAlign: 'center' }}>
                          <img
                            src={feedbackImagePreview || newFeedback.image}
                            alt="Feedback preview"
                            style={{
                              width: '100%',
                              maxWidth: '300px',
                              maxHeight: '200px',
                              borderRadius: '8px',
                              border: '2px solid #e2e8f0',
                              objectFit: 'cover',
                              display: 'block',
                              margin: '0 auto'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          {newFeedback.image && (
                            <p style={{ color: '#10b981', fontSize: '0.75rem', marginTop: '8px' }}>
                              ✓ Image uploaded successfully!
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <textarea 
                      value={newFeedback.description} 
                      onChange={(e) => setNewFeedback({ ...newFeedback, description: e.target.value })} 
                      placeholder="Description *" 
                      rows="3" 
                      className="business-form-input business-form-textarea"
                    />
                    <button type="button" onClick={addFeedback} className="business-btn business-btn-primary">
                      <i className="fi fi-rr-plus"></i>
                      <span>Add Feedback</span>
                    </button>
                  </div>
                  
                  {formData.feedbacks && Array.isArray(formData.feedbacks) && formData.feedbacks.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {formData.feedbacks.map((feedback, index) => (
                        <div key={index} style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              {feedback.image && (
                                <div style={{ marginBottom: '12px' }}>
                                  <img
                                    src={feedback.image}
                                    alt={feedback.name || 'Feedback'}
                                    style={{
                                      width: '100%',
                                      maxHeight: '200px',
                                      borderRadius: '8px',
                                      objectFit: 'cover',
                                      border: '2px solid #e2e8f0'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                              <div style={{ color: '#1e293b', fontWeight: '600', marginBottom: '4px' }}>{feedback.name}</div>
                              <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '4px' }}>{feedback.mobile_number}</div>
                              <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '4px' }}>{feedback.address}</div>
                              <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '4px' }}>{feedback.description}</div>
                            </div>
                            <button type="button" onClick={() => removeFeedback(index)} className="business-btn business-btn-secondary" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>Remove</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div className="business-form-actions">
                <button 
                  type="submit" 
                  className="business-btn business-btn-primary" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="business-spinner"></span>
                      <span>Creating Profile...</span>
                    </>
                  ) : (
                    <>
                      <span>Create Profile</span>
                      <i className="fi fi-rr-arrow-right"></i>
                    </>
                  )}
                </button>
                <button 
                  type="button" 
                  className="business-btn business-btn-secondary" 
                  onClick={() => navigate('/dashboard')}
                >
                  <span>Cancel</span>
                </button>
              </div>
            </form>
          </div>
    </AdminLayout>
  );
};

export default CreateProfile;
